'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { Button, Input } from '@velux/components-react';
import { Select } from '@/components/ui/Select';
import { OtpVerificationStep } from '@/components/onboarding/OtpVerificationStep';
import {
  decodeToken,
  validateToken,
  type MarketingLinkData,
  type OnboardingCountry,
} from '@/lib/onboardingToken';

async function fetchOnboardingCountries(): Promise<OnboardingCountry[]> {
  try {
    const response = await fetch('/api/onboarding-countries');
    if (!response.ok) return [];
    const data = await response.json();
    return data.countries ?? [];
  } catch {
    return [];
  }
}

function OnboardingForm() {
  const locale = useLocale();
  const t = useTranslations('onboarding');
  const tOtp = useTranslations('otp');
  const searchParams = useSearchParams();
  const router = useRouter();

  const token = searchParams.get('token');

  // Step: 'form' or 'otp'
  const [step, setStep] = useState<'form' | 'otp'>('form');

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    country: '',
    companyName: '',
    address: '',
    city: '',
    postalCode: '',
    phoneNumber: '',
    language: locale,
    marketingConsent: false,
    termsConsent: false,
  });
  const [countries, setCountries] = useState<OnboardingCountry[]>([]);
  const [marketingData, setMarketingData] = useState<MarketingLinkData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenError, setTokenError] = useState<string | undefined>();
  const [isTokenValidated, setIsTokenValidated] = useState(false);
  const [countryMismatchError, setCountryMismatchError] = useState<{
    userCountry: string;
    courseCountry: string;
  } | null>(null);

  // Snapshot of form values captured at submit time (for OTP step)
  const [submittedValues, setSubmittedValues] = useState<{
    email: string;
    firstName: string;
    lastName: string;
    companyName: string;
    address: string;
    city: string;
    postalCode: string;
    phoneNumber: string;
  } | null>(null);

  // Validation errors
  const [emailError, setEmailError] = useState<string | undefined>();
  const [firstNameError, setFirstNameError] = useState<string | undefined>();
  const [lastNameError, setLastNameError] = useState<string | undefined>();
  const [companyNameError, setCompanyNameError] = useState<string | undefined>();
  const [addressError, setAddressError] = useState<string | undefined>();
  const [cityError, setCityError] = useState<string | undefined>();
  const [postalCodeError, setPostalCodeError] = useState<string | undefined>();
  const [phoneError, setPhoneError] = useState<string | undefined>();
  const [termsConsentError, setTermsConsentError] = useState<string | undefined>();
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Validation functions
  const validateEmail = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return t('errors.required', { field: t('emailLabel') });
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) return t('errors.invalidEmail');
    return undefined;
  };

  const validateFirstName = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return t('errors.required', { field: t('firstNameLabel') });
    if (trimmed.length > 40) return t('errors.firstNameTooLong');
    return undefined;
  };

  const validateLastName = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return t('errors.required', { field: t('lastNameLabel') });
    if (trimmed.length > 40) return t('errors.lastNameTooLong');
    return undefined;
  };

  const validateCompanyName = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return t('errors.required', { field: t('companyNameLabel') });
    if (trimmed.length > 255) return t('errors.companyNameTooLong');
    return undefined;
  };

  const validateAddress = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return t('errors.required', { field: t('addressLabel') });
    if (trimmed.length > 500) return t('errors.addressTooLong');
    return undefined;
  };

  const validateCity = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return t('errors.required', { field: t('cityLabel') });
    if (trimmed.length > 100) return t('errors.cityTooLong');
    return undefined;
  };

  const validatePostalCode = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return t('errors.required', { field: t('postalCodeLabel') });
    const validCharsRegex = /^[A-Za-z0-9\s-]+$/;
    if (!validCharsRegex.test(trimmed)) return t('errors.invalidPostalCode');
    if (trimmed.length < 3 || trimmed.length > 10) return t('errors.postalCodeLengthInvalid');
    return undefined;
  };

  const validatePhone = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return t('errors.required', { field: t('phoneLabel') });

    // Strip spaces and hyphens for digit counting
    const digitsOnly = trimmed.replace(/[\s-]/g, '');

    if (!/^\d+$/.test(digitsOnly)) return t('errors.phoneTooShort');
    if (digitsOnly.length < 6 || digitsOnly.length > 15) return t('errors.phoneTooShort');

    return undefined;
  };

  const validateTermsConsent = (value: boolean): string | undefined => {
    if (!value) return t('errors.termsConsentRequired');
    return undefined;
  };

  // onBlur handlers
  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouchedFields((prev) => new Set(prev).add('email'));
    setEmailError(validateEmail(e.target.value));
  };

  const handleFirstNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouchedFields((prev) => new Set(prev).add('firstName'));
    setFirstNameError(validateFirstName(e.target.value));
  };

  const handleLastNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouchedFields((prev) => new Set(prev).add('lastName'));
    setLastNameError(validateLastName(e.target.value));
  };

  const handleCompanyNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouchedFields((prev) => new Set(prev).add('companyName'));
    setCompanyNameError(validateCompanyName(e.target.value));
  };

  const handleAddressBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouchedFields((prev) => new Set(prev).add('address'));
    setAddressError(validateAddress(e.target.value));
  };

  const handleCityBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouchedFields((prev) => new Set(prev).add('city'));
    setCityError(validateCity(e.target.value));
  };

  const handlePostalCodeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouchedFields((prev) => new Set(prev).add('postalCode'));
    setPostalCodeError(validatePostalCode(e.target.value));
  };

  const handlePhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouchedFields((prev) => new Set(prev).add('phoneNumber'));
    setPhoneError(validatePhone(e.target.value));
  };

  // Get dial code for a country from fetched countries
  const getDialCode = (countryCode: string): string => {
    const country = countries.find(c => c.code === countryCode.toUpperCase());
    return country?.dialCode || '';
  };

  // Get the full phone number with dial code prefix
  const getFullPhoneNumber = (localNumber: string): string => {
    const dialCode = getDialCode(formData.country);
    const trimmed = localNumber.trim();
    if (!trimmed) return '';
    return `${dialCode}${trimmed}`;
  };

  // Decode and validate token client-side on mount
  // Fetch supported countries and validate token on mount
  useEffect(() => {
    async function init() {
      // No token provided
      if (!token) {
        setTokenError('missingToken');
        setIsTokenValidated(true);
        return;
      }

      // Fetch supported countries from API
      const fetchedCountries = await fetchOnboardingCountries();
      setCountries(fetchedCountries);

      const data = decodeToken(token);
      const validation = validateToken(data, fetchedCountries);

      if (!validation.valid) {
        setTokenError(validation.error);
        setIsTokenValidated(true);
        return;
      }

      // Token is valid
      setMarketingData(data);
      setIsTokenValidated(true);

      // Pre-fill country with the canonical code (aliases like NB/SV are normalized).
      setFormData((prev) => ({
        ...prev,
        country: validation.canonicalCountry!,
      }));
    }
    init();
  }, [token]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (touchedFields.has(field)) {
      switch (field) {
        case 'email':       setEmailError(validateEmail(value)); break;
        case 'firstName':   setFirstNameError(validateFirstName(value)); break;
        case 'lastName':    setLastNameError(validateLastName(value)); break;
        case 'companyName': setCompanyNameError(validateCompanyName(value)); break;
        case 'address':     setAddressError(validateAddress(value)); break;
        case 'city':        setCityError(validateCity(value)); break;
        case 'postalCode':  setPostalCodeError(validatePostalCode(value)); break;
        case 'phoneNumber': setPhoneError(validatePhone(value)); break;
      }
    }
  };

  const handleCheckboxChange = (field: 'marketingConsent' | 'termsConsent') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setFormData((prev) => ({ ...prev, [field]: checked }));

    if (field === 'termsConsent') {
      setTermsConsentError(validateTermsConsent(checked));
    }
  };

  // Handler for country change — clear phone number since dial code changes
  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCountry = e.target.value;
    setFormData((prev) => ({ ...prev, country: newCountry, phoneNumber: '' }));
    setPhoneError(undefined);
  };

  // Send OTP email
  const sendOtp = async (email: string) => {
    const response = await fetch('/api/send-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        language: formData.language || 'en',
        country: formData.country,
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to send OTP');
    }
  };

  // Step 1: Form submit -> send OTP
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Get form values directly from the DOM to handle browser autofill
    const form = e.target as HTMLFormElement;
    const getInputValue = (id: string) => (form.querySelector(`#${id}`) as HTMLInputElement)?.value || '';

    const localPhone = getInputValue('phoneNumber') || formData.phoneNumber;
    const values = {
      email: getInputValue('email') || formData.email,
      firstName: getInputValue('firstName') || formData.firstName,
      lastName: getInputValue('lastName') || formData.lastName,
      companyName: getInputValue('companyName') || formData.companyName,
      address: getInputValue('address') || formData.address,
      city: getInputValue('city') || formData.city,
      postalCode: getInputValue('postalCode') || formData.postalCode,
      phoneNumber: getFullPhoneNumber(localPhone),
    };

    // Run all validations before submit
    const emailErr = validateEmail(values.email);
    const firstNameErr = validateFirstName(values.firstName);
    const lastNameErr = validateLastName(values.lastName);
    const companyNameErr = validateCompanyName(values.companyName);
    const addressErr = validateAddress(values.address);
    const cityErr = validateCity(values.city);
    const postalCodeErr = validatePostalCode(values.postalCode);
    const phoneErr = validatePhone(localPhone);
    const termsConsentErr = validateTermsConsent(formData.termsConsent);

    // Set all error states
    setEmailError(emailErr);
    setFirstNameError(firstNameErr);
    setLastNameError(lastNameErr);
    setCompanyNameError(companyNameErr);
    setAddressError(addressErr);
    setCityError(cityErr);
    setPostalCodeError(postalCodeErr);
    setPhoneError(phoneErr);
    setTermsConsentError(termsConsentErr);

    // Update form state with autofilled values (keep local phone number without dial code)
    setFormData((prev) => ({ ...prev, ...values, phoneNumber: localPhone }));

    // If any validation errors, don't submit
    if (emailErr || firstNameErr || lastNameErr || companyNameErr || addressErr || cityErr || postalCodeErr || phoneErr || termsConsentErr) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Send OTP email
      await sendOtp(values.email);

      // Save submitted values for the OTP step
      setSubmittedValues(values);

      // Transition to OTP step
      setStep('otp');
    } catch {
      setError(t('errors.otpSendFailed'));
    } finally {
      setLoading(false);
    }
  };

  // OTP verify callback — called by OtpVerificationStep
  const handleOtpVerify = async (otp: string): Promise<{ error?: string }> => {
    if (!submittedValues) return { error: tOtp('invalid') };

    try {
      const response = await fetch('/api/onboard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: submittedValues.email,
          firstName: submittedValues.firstName,
          lastName: submittedValues.lastName,
          country: formData.country,
          companyName: submittedValues.companyName,
          address: submittedValues.address,
          city: submittedValues.city,
          postalCode: submittedValues.postalCode,
          phoneNumber: submittedValues.phoneNumber || null,
          language: formData.language || 'en',
          accountType: marketingData?.accountType || null,
          courseId: marketingData?.courseId?.toString() || null,
          sessionId: marketingData?.sessionId?.toString() || null,
          marketingConsent: formData.marketingConsent,
          termsConsent: formData.termsConsent,
          otp,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Handle OTP errors — return error string for the component to display
        if (errorData.error === 'otp_verification_failed') {
          const reason = errorData.reason as string;
          if (reason === 'max_attempts') return { error: tOtp('maxAttempts') };
          if (reason === 'not_found') return { error: tOtp('expired') };
          return { error: tOtp('invalid') };
        }

        // Handle country mismatch - return to form step
        if (errorData.error === 'country_mismatch') {
          setCountryMismatchError({
            userCountry: errorData.userCountry || 'unknown',
            courseCountry: errorData.courseCountry || formData.country.toUpperCase(),
          });
          setStep('form');
          return {};
        }

        // Handle invalid session - return to form step
        if (errorData.error === 'invalid_session') {
          setError(t('errors.sessionNotAvailable'));
          setStep('form');
          return {};
        }

        // For other errors, still redirect to success (don't reveal info)
      }

      // Success or Partner Portal user - always redirect to success page
      const maskedEmailStr = maskEmail(submittedValues.email);

      const data = await response.json().catch(() => ({}));
      if (data.isPartnerPortalUser) {
        router.push(`/onboarding/success?email=${encodeURIComponent(maskedEmailStr)}&partnerPortal=true`);
      } else {
        router.push(`/onboarding/success?email=${encodeURIComponent(maskedEmailStr)}`);
      }
      return {};
    } catch {
      // Even on network error, redirect to success to not reveal any information
      const maskedEmailStr = maskEmail(submittedValues.email);
      router.push(`/onboarding/success?email=${encodeURIComponent(maskedEmailStr)}`);
      return {};
    }
  };

  // OTP resend callback
  const handleOtpResend = async () => {
    if (!submittedValues) return;
    try {
      await sendOtp(submittedValues.email);
    } catch {
      // Silently fail - don't reveal information
    }
  };

  // Back to form from OTP step
  const handleBackToForm = () => {
    setStep('form');
  };

  // Mask email for display (e.g., "j***@e***.com")
  const maskEmail = (email: string): string => {
    const atIndex = email.indexOf('@');
    if (atIndex <= 0) return '***';

    const localPart = email.substring(0, atIndex);
    const domainPart = email.substring(atIndex + 1);

    const maskedLocal = localPart.length > 1 ? `${localPart[0]}***` : '***';
    const dotIndex = domainPart.lastIndexOf('.');
    if (dotIndex <= 0) return `${maskedLocal}@***`;

    const domainName = domainPart.substring(0, dotIndex);
    const tld = domainPart.substring(dotIndex);
    const maskedDomain = domainName.length > 1 ? `${domainName[0]}***` : '***';

    return `${maskedLocal}@${maskedDomain}${tld}`;
  };

  // Check if there are any client-side validation errors
  const hasValidationErrors = Boolean(
    emailError || firstNameError || lastNameError || companyNameError ||
    addressError || cityError || postalCodeError || phoneError || termsConsentError
  );

  // Form is disabled if token is invalid
  const isFormDisabled = Boolean(tokenError);

  // Button is only active when all required fields are filled and terms are accepted.
  // Note: autofill users may need to interact with the form once to update React state,
  // but submit validation reads from the DOM as a fallback so no data is lost.
  const isFormComplete = Boolean(
    formData.email.trim() &&
    formData.firstName.trim() &&
    formData.lastName.trim() &&
    formData.companyName.trim() &&
    formData.address.trim() &&
    formData.city.trim() &&
    formData.postalCode.trim() &&
    formData.phoneNumber.trim() &&
    formData.termsConsent
  );

  const isSubmitDisabled = loading || isFormDisabled || hasValidationErrors || !isFormComplete;

  // Show loading while validating token
  if (!isTokenValidated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-secondary py-8 px-4">
        <div className="w-full max-w-xl bg-white p-8 shadow-card text-center">
          <p className="text-text-secondary">{t('validatingLink')}</p>
        </div>
      </div>
    );
  }

  // OTP Step
  if (step === 'otp') {
    const displayEmail = submittedValues?.email || '';
    return (
      <OtpVerificationStep
        maskedEmail={maskEmail(displayEmail)}
        onVerify={handleOtpVerify}
        onResend={handleOtpResend}
        onBack={handleBackToForm}
      />
    );
  }

  // Form Step
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-secondary py-8 px-4">
      <div className="w-full max-w-xl bg-white p-8 shadow-card">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            {t('title')}
          </h1>
          <p className="text-text-secondary">{t('subtitle')}</p>
        </div>

        {/* Token error message */}
        {tokenError && (
          <div className="mb-6 p-4 bg-velux-red/10 border border-velux-red rounded">
            <p className="text-velux-red font-medium mb-1">{t('tokenError.title')}</p>
            <p className="text-velux-red text-sm">{t(`tokenError.${tokenError}`)}</p>
          </div>
        )}

        {/* Country mismatch error message */}
        {countryMismatchError && (
          <div className="mb-6 p-4 bg-velux-red/10 border border-velux-red rounded">
            <p className="text-velux-red font-medium mb-1">{t('countryMismatchTitle')}</p>
            <p className="text-velux-red text-sm">
              {t('countryMismatchMessage', {
                userCountry: countryMismatchError.userCountry,
                courseCountry: countryMismatchError.courseCountry,
              })}
            </p>
            <p className="text-velux-red text-sm mt-2">{t('countryMismatchHelp')}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* Personal Information */}
          <div className="flex flex-col gap-4">
            {/* Email - full width */}
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              onBlur={handleEmailBlur}
              error={emailError}
              placeholder={t('emailLabel')}
              required
              disabled={isFormDisabled}
            />

            {/* First Name & Last Name - side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange('firstName')}
                onBlur={handleFirstNameBlur}
                error={firstNameError}
                required
                disabled={isFormDisabled}
                placeholder={t('firstNameLabel')}
              />
              <Input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange('lastName')}
                onBlur={handleLastNameBlur}
                error={lastNameError}
                required
                disabled={isFormDisabled}
                placeholder={t('lastNameLabel')}
              />
            </div>

          </div>

          {/* Company Information */}
          <div className="flex flex-col gap-4">
            {/* Company Name - full width */}
            <Input
              id="companyName"
              type="text"
              value={formData.companyName}
              onChange={handleChange('companyName')}
              onBlur={handleCompanyNameBlur}
              error={companyNameError}
              placeholder={t('companyNameLabel')}
              required
              disabled={isFormDisabled}
            />

            {/* Address - full width */}
            <Input
              id="address"
              type="text"
              value={formData.address}
              onChange={handleChange('address')}
              onBlur={handleAddressBlur}
              error={addressError}
              required
              disabled={isFormDisabled}
              placeholder={t('addressLabel')}
            />

            {/* Country & Phone - side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-4">
              <Select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleCountryChange}
                required
                disabled={isFormDisabled}
                label={t('countryLabel')}
                placeholder={t('countryPlaceholder')}
                options={countries.map((country) => ({
                  value: country.code,
                  label: country.name,
                }))}
              />
              <div className="flex">
                <div className="w-[80px] shrink-0">
                  <Input
                    type="tel"
                    value={getDialCode(formData.country)}
                    disabled
                    placeholder=""
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <Input
                    id="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleChange('phoneNumber')}
                    onBlur={handlePhoneBlur}
                    error={phoneError}
                    placeholder={t('phoneLabel')}
                    required
                    disabled={isFormDisabled}
                  />
                </div>
              </div>
            </div>

            {/* Postal Code & City - side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-4">
              <Input
                id="postalCode"
                type="text"
                value={formData.postalCode}
                onChange={handleChange('postalCode')}
                onBlur={handlePostalCodeBlur}
                error={postalCodeError}
                required
                disabled={isFormDisabled}
                placeholder={t('postalCodeLabel')}
                maxLength={10}
              />
              <Input
                id="city"
                type="text"
                value={formData.city}
                onChange={handleChange('city')}
                onBlur={handleCityBlur}
                error={cityError}
                required
                disabled={isFormDisabled}
                placeholder={t('cityLabel')}
              />
            </div>

          </div>

          {/* Consent Checkboxes */}
          <div className="flex flex-col gap-3 pt-2">
            {/* Privacy Notice */}
            <p className="text-xs text-text-secondary">
              {t.rich('consentNotice', {
                privacyLink: (chunks) => (
                  <a
                    href={t('termsConsentUrl')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-velux-red hover:underline"
                  >
                    {chunks}
                  </a>
                ),
              })}
            </p>

            {/* Consent Description */}
            <p className="text-xs text-text-secondary">
              {t.rich('consentDescription', {
                privacyLink: (chunks) => (
                  <a
                    href={t('termsConsentUrl')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-velux-red hover:underline"
                  >
                    {chunks}
                  </a>
                ),
              })}
            </p>

            {/* Marketing Consent */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.marketingConsent}
                onChange={handleCheckboxChange('marketingConsent')}
                disabled={isFormDisabled}
                className="mt-1 w-4 h-4 text-velux-red border-gray-300 rounded focus:ring-velux-red"
              />
              <span className="text-sm text-text-secondary">{t('marketingConsentLabel')}</span>
            </label>

            {/* Terms Consent (Required) */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.termsConsent}
                onChange={handleCheckboxChange('termsConsent')}
                disabled={isFormDisabled}
                className="mt-1 w-4 h-4 text-velux-red border-gray-300 rounded focus:ring-velux-red"
              />
              <span className="text-sm text-text-secondary">
                {t.rich('termsConsentLabel', {
                  link: (chunks) => (
                    <a
                      href={t('termsConsentUrl')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-velux-red hover:underline"
                    >
                      {chunks}
                    </a>
                  ),
                })} <span className="text-velux-red">*</span>
              </span>
            </label>
            {termsConsentError && <p className="text-velux-red text-sm -mt-2">{termsConsentError}</p>}
          </div>

          {error && <p className="text-velux-red text-sm">{error}</p>}

          <Button
            type="submit"
            variant="professional-red"
            disabled={isSubmitDisabled}
            className="w-full mt-2"
          >
            {loading ? t('submitting') : t('submitButton')}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function OnboardingPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
      <OnboardingForm />
    </Suspense>
  );
}
